import { LocationFilter } from '@/shared/components/filters/LocationFilter';
import { ManufacturerFilter } from '@/shared/components/filters/ManufacturerFilter';
import { OrderTypeFilter } from '@/shared/components/filters/OrderTypeFilter';
import { DateRangeFilter } from '@/shared/components/filters/DateRangeFilter';
import type { FrameFilters, OrderType } from '@/shared/types/domain.types';

interface ReceivingFiltersProps {
  filters: FrameFilters;
  onFilterChange: (filters: FrameFilters) => void;
}

export function ReceivingFilters({ filters, onFilterChange }: ReceivingFiltersProps) {
  const handleLocationChange = (locationIds: string[]) => {
    onFilterChange({ ...filters, location_ids: locationIds });
  };

  const handleManufacturerChange = (manufacturerIds: string[]) => {
    onFilterChange({ ...filters, manufacturer_ids: manufacturerIds });
  };

  const handleOrderTypeChange = (orderType: OrderType | 'ALL' | undefined) => {
    // 'ALL' means no filter, so set to undefined
    const filterValue = orderType === 'ALL' ? undefined : orderType;
    onFilterChange({ ...filters, order_type: filterValue });
  };

  const handleDateFromChange = (date: string) => {
    onFilterChange({ ...filters, date_from: date || undefined });
  };

  const handleDateToChange = (date: string) => {
    onFilterChange({ ...filters, date_to: date || undefined });
  };

  const handleClearFilters = () => {
    onFilterChange({});
  };

  const hasActiveFilters =
    filters.location_ids?.length ||
    filters.manufacturer_ids?.length ||
    filters.order_type ||
    filters.date_from ||
    filters.date_to;

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <LocationFilter
          selectedLocationIds={filters.location_ids || []}
          onChange={handleLocationChange}
          multiple
        />

        <ManufacturerFilter
          selectedManufacturerIds={filters.manufacturer_ids || []}
          onChange={handleManufacturerChange}
          multiple
        />

        <OrderTypeFilter
          selectedOrderType={filters.order_type || 'ALL'}
          onChange={handleOrderTypeChange}
        />

        <DateRangeFilter
          dateFrom={filters.date_from}
          dateTo={filters.date_to}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />
      </div>
    </div>
  );
}
